/**
 * Self-approved time — decision 3 (Wimpie, 2026-10-25): a manager may approve
 * their own time; each such entry is flagged, and the OTHER manager reviews
 * and signs off each month's self-approved time (T&B-1 PR 3).
 *
 * By approver and month (of the work date): the items, their stamped value,
 * which are still waiting, and every sign-off so far. A sign-off covers what
 * was self-approved when it was made; anything self-approved in that month
 * afterwards waits for another. Nobody signs off their own -- the database
 * refuses it. Managers only.
 */

import { useCallback, useEffect, useState } from 'react'
import { api, type AuthState, type ObligationTypeInUse, type SelfApprovedGroup } from '../api'
import { AppShell } from '../components/AppShell'
import { ErrorState, LoadingState } from '../components/States'
import { clientLabel, inputCls, primaryBtn, quietBtn, rand } from '../components/TimeCapture'
import { downloadCsv } from '../csv'
import { csvRand } from './ReportsPage'

const sectionCls = 'mb-4 rounded-eq-md border border-eq-border bg-white px-5 py-4'
const thCls = 'px-2 py-1 text-left font-semibold'
const tdCls = 'px-2 py-1.5 align-top'

const dayHead = (d: string) =>
  new Date(`${d}T00:00:00`).toLocaleDateString('en-ZA', { weekday: 'short', day: 'numeric', month: 'short' })
const whenLabel = (iso: string) =>
  new Date(iso).toLocaleString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })

export function SelfApprovedPage({ obligationTypes, auth }: { obligationTypes: ObligationTypeInUse[]; auth: AuthState }) {
  const [groups, setGroups] = useState<SelfApprovedGroup[] | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const signedInManager = auth.mode === 'cognito' && auth.user?.role === 'manager'

  const load = useCallback(async () => {
    try {
      setGroups((await api.selfApproved()).groups)
      setLoadError(null)
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : String(e))
    }
  }, [])
  useEffect(() => {
    if (signedInManager) void load()
  }, [load, signedInManager])

  const signOff = async (g: SelfApprovedGroup, note: string) => {
    if (busy) return
    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      const r = await api.signOffSelfApproved({ approverId: g.approver.id, month: g.month, note })
      setNotice(`Signed off ${r.itemCount} self-approved ${r.itemCount === 1 ? 'item' : 'items'} of ${g.approver.name} for ${g.label}.`)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      await load()
      setBusy(false)
    }
  }

  const waitingTotal = groups?.reduce((a, g) => a + g.waiting, 0) ?? 0

  return (
    <AppShell
      obligationTypes={obligationTypes}
      auth={auth}
      eyebrow="Time"
      title="Self-approved time"
      subtitle="Time a manager approved for themselves. The other manager reviews each month and signs it off; the sign-off is kept."
    >
      {auth.mode === 'anonymous' ? (
        <ErrorState message="Sign-off records who signed, so it needs sign-in. This server is running without Cognito (local development)." />
      ) : !signedInManager ? (
        <ErrorState message="Only a manager may see self-approved time." />
      ) : loadError ? (
        <ErrorState message={loadError} />
      ) : groups === null ? (
        <LoadingState what="self-approved time" />
      ) : (
        <>
          {error && (
            <div className="mb-4">
              <ErrorState message={error} />
            </div>
          )}
          {notice && <div className="mb-4 rounded-eq-md border border-eq-border bg-white px-4 py-2 text-[13.5px]">{notice}</div>}
          {groups.length === 0 ? (
            <p className="text-[14px] text-eq-neutral">No time has been self-approved.</p>
          ) : (
            <>
              <div className="mb-3 flex justify-end gap-2 print:hidden">
                <button
                  type="button"
                  className={quietBtn}
                  onClick={() =>
                    downloadCsv(
                      'self-approved-time.csv',
                      ['Approver', 'Month', 'Kind', 'Date', 'Hours', 'What', 'Client', 'Approved at', 'Value', 'Waiting for sign-off', 'Signed off by'],
                      groups.flatMap((g) =>
                        g.items.map((i) => [
                          g.approver.name,
                          g.label,
                          i.kind,
                          i.date,
                          i.hours ?? '',
                          i.what,
                          i.client ? clientLabel(i.client) : '',
                          i.approvedAt,
                          csvRand(i.valueCents),
                          i.waiting ? 'yes' : 'no',
                          g.signoffs.map((s) => `${s.reviewer} ${s.signedAt}`).join('; '),
                        ])
                      )
                    )
                  }
                >
                  Export CSV
                </button>
                <button type="button" className={quietBtn} onClick={() => window.print()}>
                  Print
                </button>
              </div>
              <p className="mb-4 mt-0 text-[13.5px]">
                {waitingTotal === 0 ? 'Everything self-approved has been signed off.' : `${waitingTotal} self-approved ${waitingTotal === 1 ? 'item is' : 'items are'} waiting for sign-off.`}
              </p>
              {groups.map((g) => (
                <Group key={`${g.approver.id}|${g.month}`} group={g} busy={busy} viewerIsApprover={auth.user?.id === g.approver.id} onSignOff={signOff} />
              ))}
            </>
          )}
        </>
      )}
    </AppShell>
  )
}

function Group({
  group: g,
  busy,
  viewerIsApprover,
  onSignOff,
}: {
  group: SelfApprovedGroup
  busy: boolean
  viewerIsApprover: boolean
  onSignOff: (g: SelfApprovedGroup, note: string) => Promise<void>
}) {
  const [note, setNote] = useState('')
  return (
    <section className={sectionCls}>
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="m-0 font-heading text-[15px] font-bold text-eq-navy">
          {g.approver.name} <span className="font-normal text-eq-neutral">· {g.label}</span>
        </h2>
        <span className="text-[13px] tabular-nums text-eq-neutral">
          {g.items.length} {g.items.length === 1 ? 'item' : 'items'} · {g.hours} h · {rand(g.valueCents)}
          {g.waiting > 0 ? (
            <span className="ml-2 rounded-eq-pill bg-eq-warning-bg px-2 py-0.5 text-[11px] font-semibold text-eq-warning">{g.waiting} waiting</span>
          ) : (
            g.items.length > 0 && <span className="ml-2 rounded-eq-pill bg-eq-success-bg px-2 py-0.5 text-[11px] font-semibold text-eq-success">signed off</span>
          )}
        </span>
      </div>
      {g.items.length > 0 && (
        <table className="w-full border-collapse text-[13.5px]">
          <thead className="text-[12px] text-eq-neutral">
            <tr>
              <th className={thCls}>Day</th>
              <th className={thCls}>Hours</th>
              <th className={thCls}>What</th>
              <th className={thCls}>Client</th>
              <th className={thCls}>Approved</th>
              <th className={`${thCls} text-right`}>Value</th>
            </tr>
          </thead>
          <tbody>
            {g.items.map((i) => (
              <tr key={`${i.kind}:${i.id}`} className={['border-t border-eq-border', i.waiting ? 'bg-eq-warning-bg/40' : ''].join(' ')}>
                <td className={`${tdCls} whitespace-nowrap`}>{dayHead(i.date)}</td>
                <td className={`${tdCls} tabular-nums`}>{i.hours ?? <span className="text-[11px] text-eq-helper">disbursement</span>}</td>
                <td className={tdCls}>{i.what}</td>
                <td className={tdCls}>{i.client ? clientLabel(i.client) : '—'}</td>
                <td className={`${tdCls} whitespace-nowrap text-[12px] text-eq-neutral`}>
                  {whenLabel(i.approvedAt)}
                  {i.waiting && <div className="text-eq-warning">waiting for sign-off</div>}
                </td>
                <td className={`${tdCls} text-right tabular-nums`}>{rand(i.valueCents)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {g.signoffs.length > 0 && (
        <ul className="mb-0 mt-3 list-none p-0 text-[12.5px] text-eq-neutral">
          {g.signoffs.map((s) => (
            <li key={s.signedAt}>
              Signed off by {s.reviewer}, {whenLabel(s.signedAt)} — {s.itemCount} {s.itemCount === 1 ? 'item' : 'items'}
              {s.note && <> — {s.note}</>}
            </li>
          ))}
        </ul>
      )}
      {g.waiting > 0 &&
        (g.maySignOff ? (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <input className={`${inputCls} w-80`} placeholder="Note (optional)" value={note} maxLength={2000} onChange={(e) => setNote(e.target.value)} />
            <button className={primaryBtn(busy)} disabled={busy} onClick={() => void onSignOff(g, note)}>
              Sign off {g.waiting} {g.waiting === 1 ? 'item' : 'items'}
            </button>
          </div>
        ) : (
          viewerIsApprover && <p className="mb-0 mt-3 text-[12.5px] text-eq-helper">This is your own self-approved time: another manager signs it off.</p>
        ))}
    </section>
  )
}
